import { AVAILABLE_MODULES } from '../types/package';
import { UserModulePermissions } from '../types/user';

// Navigációs modulok (NavBar.tsx-ből)
const NAVIGATION_MODULES = [
  'customers',      // nav.customers
  'suppliers',      // nav.suppliers  
  'products',       // nav.products, nav.categories, nav.warranty
  'inventory',      // nav.warehouse, nav.inventory
  'quotations',     // nav.offers, nav.ai_quotations, nav.orders, nav.delivery_notes
  'deals',          // nav.deals
  'sales',          // nav.sales
  'contracts',      // nav.contracts
  'templates',      // nav.templates
  'forms',          // nav.formBuilder, nav.myForms
  'projects',       // nav.projects
  'tasks',          // nav.tasks
  'worksheets',     // nav.worksheets
  'billing',        // nav.billing
  'reports',        // nav.reports
  'settings',       // nav.settings
  'admin',          // nav.system_admin
  'notes',          // nav.notes, nav.richNotes
  'appointments',   // nav.appointments
  'customerPortal'  // nav.customerPortal
];

// UserModulePermissions típusból kinyert modulok
const USER_PERMISSION_MODULES: (keyof UserModulePermissions)[] = [
  'customers',
  'suppliers', 
  'products',
  'inventory',
  'quotations',
  'deals',
  'sales',
  'contracts',
  'templates',
  'forms',
  'worksheets',
  'billing',
  'reports',
  'settings',
  'admin',
  'tasks',
  'projects',
  'notes',
  'appointments',
  'customerPortal'
];

// Package modulok (AVAILABLE_MODULES-ból)
const PACKAGE_MODULES = AVAILABLE_MODULES.map(module => module.id);

/**
 * Ellenőrzi, hogy minden modul konzisztens-e a különböző definíciók között
 */
export function validateModuleConsistency(): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // 1. Ellenőrizzük, hogy minden navigációs modul létezik-e a package modulokban
  NAVIGATION_MODULES.forEach(navModule => {
    if (!PACKAGE_MODULES.includes(navModule)) {
      errors.push(`Navigációs modul "${navModule}" nincs definiálva a AVAILABLE_MODULES-ban`);
    }
  });

  // 2. Ellenőrizzük, hogy minden navigációs modul létezik-e a UserModulePermissions-ban
  NAVIGATION_MODULES.forEach(navModule => {
    if (!USER_PERMISSION_MODULES.includes(navModule as keyof UserModulePermissions)) {
      errors.push(`Navigációs modul "${navModule}" nincs definiálva a UserModulePermissions-ban`);
    }
  });

  // 3. Ellenőrizzük, hogy minden package modul létezik-e a UserModulePermissions-ban
  PACKAGE_MODULES.forEach(packageModule => {
    if (!USER_PERMISSION_MODULES.includes(packageModule as keyof UserModulePermissions)) {
      warnings.push(`Package modul "${packageModule}" nincs definiálva a UserModulePermissions-ban`);
    }
  });

  // 4. Ellenőrizzük, hogy minden UserModulePermissions modul létezik-e a package modulokban
  USER_PERMISSION_MODULES.forEach(permModule => {
    if (!PACKAGE_MODULES.includes(permModule)) {
      warnings.push(`UserModulePermissions modul "${permModule}" nincs definiálva a AVAILABLE_MODULES-ban`);
    }
  });

  // 5. Core modulok ellenőrzése
  const coreModules = AVAILABLE_MODULES.filter(m => m.isCore).map(m => m.id);
  const expectedCoreModules = ['customers', 'settings'];
  
  expectedCoreModules.forEach(expectedCore => {
    if (!coreModules.includes(expectedCore)) {
      errors.push(`Elvárható core modul "${expectedCore}" nincs megjelölve core-ként`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Ellenőrzi, hogy egy adott modul elérhető-e
 */
export function isModuleAvailable(moduleId: string): boolean {
  return PACKAGE_MODULES.includes(moduleId);
}

/**
 * Visszaadja az összes elérhető modul ID-t
 */
export function getAllAvailableModules(): string[] {
  return [...PACKAGE_MODULES];
}

/**
 * Visszaadja a core modulokat
 */
export function getCoreModules(): string[] {
  return AVAILABLE_MODULES.filter(m => m.isCore).map(m => m.id);
}

/**
 * Visszaadja a modulokat kategória szerint
 */
export function getModulesByCategory(category: string) {
  return AVAILABLE_MODULES.filter(m => m.category === category);
}

/**
 * Ellenőrzi, hogy egy csomag moduljai érvényesek-e
 */
export function validatePackageModules(modules: string[]): {
  isValid: boolean;
  invalidModules: string[];
  missingCoreModules: string[];
} {
  const coreModules = getCoreModules();
  const invalidModules = modules.filter(m => !isModuleAvailable(m));
  const missingCoreModules = coreModules.filter(core => !modules.includes(core));

  return {
    isValid: invalidModules.length === 0 && missingCoreModules.length === 0,
    invalidModules,
    missingCoreModules
  };
}

// Konzisztencia ellenőrzés futtatása fejlesztési módban
if (process.env.NODE_ENV === 'development') {
  const validation = validateModuleConsistency();
  
  // if (!validation.isValid) {
  //   console.error('🚨 Modul konzisztencia hibák:', validation.errors);
  // }
  
  // if (validation.warnings.length > 0) {
  //   console.warn('⚠️ Modul konzisztencia figyelmeztetések:', validation.warnings);
  // }
  
  // if (validation.isValid && validation.warnings.length === 0) {
  //   console.log('✅ Minden modul konzisztens!');
  // }
}
